import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultEstatePlanningGlossaryData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'glossary-section',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'glossary-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 5,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'glossary-list-items-section-left',
                    type: 'ListItemsSection',
                    props: {},
                    children: {
                      default: [
                        {
                          id: 'glossary-item-will',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Will',
                            description:
                              'A legal document that outlines how your assets should be distributed after your death and names an executor to handle your estate. Can appoint guardians for minor children, if applicable.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-trust',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Trust',
                            description:
                              'A legal arrangement in which one party (the trustee) holds and manages assets on behalf of another (the beneficiary), according to the terms set by the person who created the trust (the grantor). Trusts can be used during your lifetime and/or after your death.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-revocable-trust',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Revocable living trust',
                            description:
                              'A flexible trust that can be modified or canceled during life. Allows assets to pass to beneficiaries without going through probate and provides more privacy than a will. Can manage assets during your lifetime if you become incapacitated.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-irrevocable-trust',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Irrevocable trust',
                            description:
                              'A trust that generally cannot be changed or revoked once established. Often used to reduce estate taxes, protect assets, or control how assets are used over time.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-executor',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Executor',
                            description:
                              'The person named in your will who is responsible for carrying out its instructions and managing your estate through probate.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-trustee',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Trustee',
                            description:
                              'The individual or institution responsible for managing and distributing the assets held in a trust.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-guardianship',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Guardianship designations (if applicable)',
                            description:
                              'If you have minor children, this document appoints a guardian to care for them if you pass away or become incapacitated.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                      ],
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'glossary-list-items-section-right',
                    type: 'ListItemsSection',
                    props: {},
                    children: {
                      default: [
                        {
                          id: 'glossary-item-medical-poa',
                          type: 'PresentationListItem',
                          props: {
                            title:
                              'Healthcare power of attorney (medical POA / healthcare proxy)',
                            description:
                              "A document that names someone to make medical decisions on your behalf if you're unable to.",
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-living-will',
                          type: 'PresentationListItem',
                          props: {
                            title:
                              'Living will (advanced healthcare directive)',
                            description:
                              'A document that outlines your medical treatment preferences in case you become terminally ill or incapacitated. Addresses life-sustaining treatments and end-of-life care.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-durable-poa',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Durable power of attorney (POA)',
                            description:
                              'A document that grants someone authority to handle your financial and legal matters if you become incapacitated.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-limited-poa',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Limited power of attorney (limited POA)',
                            description:
                              "A power of attorney that grants someone authority to act on your behalf for a specific purpose or limited time, such as signing a document while you're out of town.",
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-beneficiary',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Beneficiary designation',
                            description:
                              'A form used to name who receives specific assets — like retirement accounts or life insurance — outside of your will. Beneficiary designations override wills, so it is important to keep them current.',
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                        {
                          id: 'glossary-item-probate',
                          type: 'PresentationListItem',
                          props: {
                            title: 'Probate',
                            description:
                              "The legal process of validating a will and distributing a deceased person's assets under court supervision.",
                            iconType: 'check',
                            iconColor: 'PRIMARY',
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
